import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import { cartKey, updateCartItemOptions } from '@lib/api/options/cart';
import { MutationCallbackOptions } from '@lib/api/options/types';
import AppError from '@lib/app-error';
import { CART_ERROR_CODE } from '@lib/types';
import { getErrorMessage } from '@lib/utils/error-handler';

import { createQuantityUpdateQueue } from './quantity-update-queue';
import { refreshCartOnUnavailableOffer } from './refresh-cart-on-unavailable-offer';

export const useUpdateCartItem = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation(updateCartItemOptions());

  const executeUpdate = useCallback(
    (input: { id: string; quantity: number }) =>
      mutateAsync(input, {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: cartKey }),
        onError: async (error) => {
          if (await refreshCartOnUnavailableOffer(queryClient, error)) {
            message.warning(
              'A oferta não está mais disponível e foi removida do carrinho.',
            );

            return;
          }

          if (
            error instanceof AppError &&
            (error.code === CART_ERROR_CODE.OFFER_LIMIT_REACHED ||
              error.code === CART_ERROR_CODE.STORE_PURCHASE_LIMIT_REACHED)
          ) {
            message.warning(
              getErrorMessage(
                error,
                'A quantidade solicitada excede o limite permitido.',
              ),
            );

            return;
          }

          message.error('Não foi possível atualizar a quantidade.');
        },
      }),
    [mutateAsync, queryClient, message],
  );

  const [pendingUpdates, setPendingUpdates] = useState(0);
  const queue = useMemo(
    () => createQuantityUpdateQueue(executeUpdate),
    [executeUpdate],
  );

  const handleOnUpdateCartItem = async (
    input: { id: string; quantity: number },
    options?: MutationCallbackOptions,
  ) => {
    setPendingUpdates((count) => count + 1);

    try {
      await queue(input).then(
        () => options?.onCompleted?.(),
        (error: unknown) => {
          options?.onError?.(error);
          throw error;
        },
      );
    } finally {
      setPendingUpdates((count) => count - 1);
    }
  };

  return {
    updateCartItem: handleOnUpdateCartItem,
    updatingCartItem: isPending || pendingUpdates > 0,
  };
};
